import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DefaultPaginationInfo, MarkProductForCacheSectionNames} from "../../../../../../../../core/constants/enums";
import ImportingLottieAnimation from "../../../../../../../components/app-specific/lottie-animations/importing";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import Api from "../../../../../../../../core/services/api/api";
import {ReactComponent as Passed} from "../../../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../../../assets/images/validations/failed.svg";
import Utils, {createUUId, exportCSVFile} from "../../../../../../../../core/services/utils/utils";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import {Col, Row} from "reactstrap";
import {ReactComponent as SuccessSvg} from "../../../../../../../../assets/images/art-like/success.svg";
import {ReactComponent as FailedSvg} from "../../../../../../../../assets/images/art-like/failed.svg";
import {ReactComponent as ProblemSvg} from "../../../../../../../../assets/images/art-like/problem.svg";
import Button from "../../../../../../../components/base/button/button";
import {Box, CircularProgress, LinearProgress, SvgIcon, Tooltip} from "@mui/material";
import {ReactComponent as Excel} from "../../../../../../../../assets/images/excel.svg";
import SearchInput from "../../../../../../../components/base/input/search_input";
import {ReactComponent as Warning} from "../../../../../../../../assets/images/importing-process-warning-icon.svg";
import Texts from "../../../../../../../../core/models/static/texts/texts";
import DateUtils from "../../../../../../../../core/services/date-utils";
import ToastService from "../../../../../../../../core/services/toast";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import ApiResponseCodes from "../../../../../../../../core/models/static/api-response-codes";
import useInteractiveCorrection from "../../components/interactive-correction-dialog/use-interactive-correction";
import InteractiveCorrectionDialog from "../../components/interactive-correction-dialog";
import {ImportingDataTypes, ImportingProcessStatus} from "../../types";
import {getProductWarehouseAssignmentExecutionOrder} from "../../engine/shared/import-warehouse-execution-order";
import {getSkippedFieldLabelsForRow} from "../../engine/preventative-interactions/shared";
import {isDuplicatePartCodeApiResponse} from "../../engine/shared/product-code-import-helpers";
import {useTranslation} from "react-i18next";
import {getResultingIsSerializedFromToggle} from "../../engine/transformers/shared/serializability-boolean";
import {joinDelimitedList} from "../../engine/transformers/shared/inventory-availability-validation";
import {getImportFieldDisplayLabel} from "../../engine/shared/import-field-display-label";

/**
 * Default pagination configuration for the data grid
 */
const _initialPaginationInfo = {
    ...DefaultPaginationInfo,
    pageSize: 10,
}

/**
 * Configuration for saving data grid state
 */
const dataGridSaveConfig = {
    name: 'ImportDataViewImportingSectionDataGrid',
    version: 4,
}

const ImportingSection = ({
                              state,
                              processStatus,
                              setProcessStats,
                              isDataTypeApiCallsBatched,
                              getBatchedApiRequestRecords,
                              usableRecords,
                              importProgressTotal,
                              originalApiRequests,
                              /** InteractiveSectionConfig[] for the current data type ([] if not supported) */
                              interactiveSections,
                              interactiveCorrectionEnabled,
                              setInteractiveCorrectionEnabled,
                              /** Updater for apiRequests in the parent ImportDataView */
                              setApiRequests,
                              /** Pre-loaded data bundle for getFieldOptions (allCategories, allIndustries…) */
                              staticDataContext,
                              /** Row indices removed by the user in preventative correction */
                              userSkippedRowIndices = [],
                              /** Per-row skipped import fields from preventative correction */
                              userSkippedFieldCells = [],
                              obsoleteRowIndexSet = new Set(),
                              skippedFieldLabelByKey = {},
                              serialImportSplitter = ',',
                          }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.importingSection'});
    const {t: tSettingsImport} = useTranslation('views', {keyPrefix: 'app-specific.settingsImport'});
    // State Management
    const [processedData, setProcessedData] = useState([]);     // Tracks processed records
    const [exporting, setExporting] = useState(false);          // Controls export process state
    const [keyword, setKeyword] = useState('');                  // Search keyword for filtering
    const timeSpentInMs = useRef(0);            // Tracks total import duration
    const isMounted = useIsMounted();                                            // Component mount status check
    const currentIndexRef = useRef(0);
    const isPausedRef = useRef(false);

    const userSkippedRowIndexSet = useMemo(() => new Set(userSkippedRowIndices ?? []), [userSkippedRowIndices],);

    const IMPORT_SKIPPED_BY_USER_MESSAGE = useMemo(() => "Skipped by User's Choice", []);
    const IMPORT_NO_IMPORTABLE_DATA_MESSAGE = useMemo(
        () => "Skipped: no importable data for this row",
        [],
    );

    const hasSkippedFieldCells = (userSkippedFieldCells?.length ?? 0) > 0;

    const skippedFieldKeysByRow = useMemo(() => {
        /** @type {Map<number, Set<string>>} */
        const map = new Map();
        for (const cell of userSkippedFieldCells ?? []) {
            const rowIndex = Number(cell.rowIndex);
            if (Number.isNaN(rowIndex) || !cell.fieldKey) {
                continue;
            }
            if (!map.has(rowIndex)) {
                map.set(rowIndex, new Set());
            }
            map.get(rowIndex).add(cell.fieldKey);
        }
        return map;
    }, [userSkippedFieldCells]);

    const isImportedFieldSkipped = useCallback((rowIndex, fieldName) => {
        if (rowIndex == null || !fieldName) {
            return false;
        }
        return skippedFieldKeysByRow.get(rowIndex)?.has(fieldName) ?? false;
    }, [skippedFieldKeysByRow]);

    /**
     * Field names that should render the product information popover in the import grid.
     * @type {function(Array): Set<string>}
     */
    const getProductPopoverFieldNames = useCallback((mappingFields) => {
        const names = new Set();
        for (const field of mappingFields ?? []) {
            if (field.showProductInformationPopover && field.name) {
                names.add(field.name);
            }
        }
        return names;
    }, []);

    const productPopoverFieldNames = useMemo(
        () => getProductPopoverFieldNames(state.mappingFields),
        [getProductPopoverFieldNames, state.mappingFields],
    );

    const fieldDisplayLabelContext = useMemo(() => ({
        mappingFields: state.mappingFields ?? [],
        allPriceLevels: staticDataContext?.allPriceLevels ?? [],
        formatPriceLevelLabel: (title, levelIndex) => tSettingsImport('fieldDisplayLabel.priceLevelSuffix', {title, levelIndex}),
    }), [state.mappingFields, staticDataContext?.allPriceLevels, tSettingsImport]);

    const mappedFields = useMemo(
        () => (state.mappingFields ?? []).filter((field) => state.columnMapping?.[field.name] != null),
        [state.mappingFields, state.columnMapping],
    );

    /**
     * Builds the value for the "Skipped Fields" column for a given row by aggregating the labels of all fields that were marked as skipped in that row during preventative correction. This function is used to provide users with a clear summary of which specific fields were skipped for each record in the final report.
     * @type {function(*): string|string}
     */
    const buildSkippedFieldsColumnValue = useCallback((rowIndex) => {
        const labels = getSkippedFieldLabelsForRow(rowIndex, userSkippedFieldCells, skippedFieldLabelByKey);
        return labels.length ? labels.join('; ') : '';
    }, [skippedFieldLabelByKey, userSkippedFieldCells]);

    /**
     * Records a skipped entry in the processed data state when a user chooses to skip a record during preventative correction. This allows skipped records to appear in the final report with an appropriate status and message.
     * @type {(function(*, *=, *=, *=): void)|*}
     */
    const recordSkippedRowOutcome = useCallback((importedFileEntry, {
        errorMessage,
        skipReason = 'row_skip',
        rowIndex = null,
    } = {}) => {
        const skippedFieldsValue = rowIndex == null ? '' : buildSkippedFieldsColumnValue(rowIndex);
        setProcessedData((prevState) => [
            {
                ...importedFileEntry,
                ...(skippedFieldsValue ? {'Skipped Fields': skippedFieldsValue} : {}),
                id: createUUId(),
                status: false,
                errorMessage,
                __raw__: {
                    skipReason,
                    rowIndex,
                    skippedFields: skippedFieldsValue
                        ? skippedFieldsValue.split('; ').filter(Boolean)
                        : [],
                },
            },
            ...prevState,
        ]);
    }, [buildSkippedFieldsColumnValue]);

    const recordSkippedByUser = useCallback((importedFileEntry, rowIndex = null) => {
        recordSkippedRowOutcome(importedFileEntry, {
            errorMessage: IMPORT_SKIPPED_BY_USER_MESSAGE,
            skipReason: 'row_skip',
            rowIndex,
        });
    }, [IMPORT_SKIPPED_BY_USER_MESSAGE, recordSkippedRowOutcome]);

    /**
     * Keeps the paused status in sync with processStatus prop changes. This ensures that if the parent component updates the processStatus to 'paused', the importing loop will respect that change.
     */
    useEffect(() => {
        isPausedRef.current = processStatus === ImportingProcessStatus.paused;
    }, [processStatus]);

    /**
     * Checks if interactive correction is enabled based on partner configuration. This function is passed down to the interactive correction hook to determine if the correction flow should be activated for failed records.
     * @type {Function}
     */
    const getInteractiveCorrectionEnabled = useCallback(() => {
        return window.partner?.importingInteractiveCorrectionEnabled ?? false;
    }, [])

    // ── Interactive correction hook ────────────────────────────────────────────
    const {
        dialogState,
        requestCorrectionIfNeeded,
        applyAndRetry,
        skip,
    } = useInteractiveCorrection({
        interactiveSections: interactiveSections ?? [],
        currentIndexRef,
        setApiRequests,
        staticDataContext,
        getExternalEnabled: getInteractiveCorrectionEnabled,
    });

    /**
     * Defines the structure of data grid columns
     * Includes mapped value columns plus status and error columns
     */
    const columns = useMemo(() =>
        [
            ...(mappedFields.map((field) => ({
                title: getImportFieldDisplayLabel(field, fieldDisplayLabelContext),
                name: field.name,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                pinned: false,
                sortable: false,
                visibilityToggleable: false,
                width: productPopoverFieldNames.has(field.name) ? 180 : 120,
                savable: false,
            }))),
            ...(hasSkippedFieldCells
                ? [{
                    title: t('skippedFieldsColumn'),
                    name: 'Skipped Fields',
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    pinned: false,
                    sortable: false,
                    visibilityToggleable: false,
                    width: 220,
                    savable: false,
                }]
                : []),
            // Status column
            {
                title: "Status",
                name: 'status',
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: false,
                sortable: false,
                visibilityToggleable: false,
                width: 40,
            },
            // Error description column
            {
                title: "Error Description",
                name: 'errorMessage',
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                visibilityToggleable: false,
                width: 300,
            }
        ], [mappedFields, fieldDisplayLabelContext, hasSkippedFieldCells, productPopoverFieldNames, t]);

    const [dataGridColumns, , dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Initializes pagination based on saved grid state or defaults
     */
    const initialPaginationInfo = useMemo(() =>
            dataGridPageSize
                ? ({..._initialPaginationInfo, pageSize: dataGridPageSize})
                : _initialPaginationInfo,
        [dataGridPageSize]);

    const [_paginationInfo, _setPaginationInfo] = useState(initialPaginationInfo);

    /**
     * Computed pagination information for the data grid
     */
    const paginationInfo = useMemo(() => ({
        currentPage: _paginationInfo.currentPage,
        pageSize: _paginationInfo.pageSize,
        length: processedData.length,
    }), [_paginationInfo.currentPage, _paginationInfo.pageSize, processedData.length]);

    /**
     * Filters and paginates data based on search keyword
     */
    const visibleData = useMemo(() => {
        const _keyword = keyword?.trim()?.toLowerCase() ?? '';
        const filteredData = processedData.filter(e =>
            Object
                .values(e)
                .filter(d => typeof e !== 'boolean' && !!d)
                .some(e => (e.toString()?.trim()?.toLowerCase().includes(_keyword)))
        );
        return filteredData.slice((paginationInfo.currentPage - 1) * paginationInfo.pageSize, ((paginationInfo.currentPage) * paginationInfo.pageSize));
    }, [keyword, processedData, paginationInfo.currentPage, paginationInfo.pageSize]);

    /**
     * Creates a response entity for processed data
     * @type {(function({importedFileEntry: *, response: *, selectedDataType: *}): (*&{id: string, status: *, errorMessage: string|string}))|*}
     */
    const createResponseEntity = useCallback(({importedFileEntry, response, selectedDataType, requestRecord, rowIndex = null}) => {
        const withRowIndex = (entity) => ({
            ...entity,
            __raw__: {
                ...(entity.__raw__ ?? {}),
                ...(rowIndex == null ? {} : {rowIndex}),
            },
        });
        switch (selectedDataType.id) {
            case ImportingDataTypes.partVisibility: {
                const recordResponse = response?.data?.find(data => data.partNumber === (importedFileEntry.partNumber?.toUpperCase() ?? ''));
                return withRowIndex({
                    ...importedFileEntry,
                    id: createUUId(),
                    status: !!recordResponse,
                    errorMessage: !!recordResponse ? "" : Texts.invalidData,
                    __raw__: {
                        request: requestRecord,
                        response: recordResponse,
                        importedFileEntry: importedFileEntry,
                    },
                });
            }
            case ImportingDataTypes.productsFromAnotherPartner: {
                const partnerName =
                    requestRecord?.partnerName ??
                    importedFileEntry.partnerName ??
                    '';
                const createdPartNumber = response?.data?.partNumber;
                const succeeded = response?.resultFlag ?? false;
                return withRowIndex({
                    ...importedFileEntry,
                    partnerName,
                    ...(createdPartNumber != null && createdPartNumber !== ''
                        ? {'Created Part Number': createdPartNumber}
                        : {}),
                    id: createUUId(),
                    status: succeeded,
                    errorMessage: succeeded
                        ? (response?.message ?? '')
                        : (response?.message ?? Texts.invalidData),
                    __raw__: {
                        request: requestRecord,
                        response: response,
                        importedFileEntry: importedFileEntry,
                    },
                });
            }
            case ImportingDataTypes.productFormAssignment: {
                const formTitle =
                    requestRecord?.formTitle ??
                    importedFileEntry.formTitle ??
                    '';
                return withRowIndex({
                    ...importedFileEntry,
                    formTitle,
                    id: createUUId(),
                    status: response?.resultFlag ?? false,
                    errorMessage: response?.resultFlag ? '' : (response?.message ?? Texts.invalidData),
                    __raw__: {
                        request: requestRecord,
                        response: response,
                        importedFileEntry: importedFileEntry,
                    },
                });
            }
            case ImportingDataTypes.productRelationAssignment: {
                const relationTitle =
                    requestRecord?.relationTitle ??
                    importedFileEntry.relationTitle ??
                    '';
                return withRowIndex({
                    ...importedFileEntry,
                    relationTitle,
                    id: createUUId(),
                    status: response?.resultFlag ?? false,
                    errorMessage: response?.resultFlag ? '' : (response?.message ?? Texts.invalidData),
                    __raw__: {
                        request: requestRecord,
                        response: response,
                        importedFileEntry: importedFileEntry,
                    },
                });
            }
            case ImportingDataTypes.productCodeAssignment:
                return withRowIndex({
                    ...importedFileEntry,
                    id: createUUId(),
                    status: response?.resultFlag ?? false,
                    errorMessage: response?.resultFlag ? (response?.message ?? '') : (response?.message ?? Texts.invalidData),
                    __raw__: {
                        request: requestRecord,
                        response: response,
                        importedFileEntry: importedFileEntry,
                    },
                });
            case ImportingDataTypes.setInventoryAvailability: {
                const batchSucceeded = response?.resultFlag ?? false;
                const responseItems = response?.data?.items ?? [];
                const batchItemIndex = requestRecord?.__batchItemIndex ?? 0;
                const matchedItem = responseItems[batchItemIndex]
                    ?? responseItems.find((item) =>
                        (item?.partNo ?? '').toUpperCase() === (requestRecord?.partNo ?? '').toUpperCase(),
                    );
                const serialsAdded = joinDelimitedList(matchedItem?.serialsAdded, serialImportSplitter);
                const serialsRemoved = joinDelimitedList(matchedItem?.serialsRemoved, serialImportSplitter);
                return withRowIndex({
                    ...importedFileEntry,
                    'From Qty': matchedItem?.fromQty ?? '',
                    'To Qty': matchedItem?.toQty ?? '',
                    'Delta': matchedItem?.delta ?? '',
                    'Serials Added': serialsAdded,
                    'Serials Removed': serialsRemoved,
                    id: createUUId(),
                    status: batchSucceeded,
                    errorMessage: batchSucceeded ? '' : (response?.message ?? Texts.invalidData),
                    __raw__: {
                        request: requestRecord,
                        response: response,
                        responseItem: matchedItem,
                        importedFileEntry: importedFileEntry,
                        batchItemIndex,
                    },
                });
            }
            default: {
                return withRowIndex({
                    ...importedFileEntry,
                    id: createUUId(),
                    status: response?.resultFlag ?? false,
                    errorMessage: !!response?.resultFlag ? '' : (response?.message ?? Texts.invalidData),
                    __raw__: {
                        request: requestRecord,
                        response: response,
                        importedFileEntry: importedFileEntry,
                    },
                });
            }
        }
    }, [serialImportSplitter])

    /**
     * Determines appropriate API callback based on selected data type
     */
    const apiCallback = useMemo(() => {
        switch (state.selectedDataType.id) {
            case ImportingDataTypes.partDescription:
                return async (...args) => {
                    const request = args[0];
                    const response = await Api.upsertOperatorPartDescription(args[0], false);
                    if (response?.resultFlag) {
                        void Api.markPartsForCache({partIds: [request.partId], sectionNames: [MarkProductForCacheSectionNames.description]})
                    }
                    return response;
                };
            case ImportingDataTypes.partTitle:
                return (...args) => Api.updatePartTitle(args[0]);
            case ImportingDataTypes.partShortDescription:
                return (...args) => Api.updateOperatorPartShortDescription(args[0], false);
            case ImportingDataTypes.partNotes:
                return (...args) => Api.createOperatorPartNote(args[0], false);
            case ImportingDataTypes.partUPC:
                return async (...args) => {
                    const request = args[0];
                    const upcList = request.upcList ?? (request.upc ? [request.upc] : []);
                    const response = {
                        resultFlag: true,
                        message: '',
                    }
                    for (const upc of upcList) {
                        const addResponse = await Api.addPartUpc({partId: request.partId, upcCode: upc,}, false);
                        if (!addResponse?.resultFlag) {
                            response.resultFlag = false;
                            if (!response.message) {
                                response.message = `Failed to add the following UPCs:`;
                            }
                            response.message += ` ${upc} (${addResponse?.message || 'Error'}), `;
                        }
                    }
                    if (response?.resultFlag) {
                        response.message = `Successfully added ${upcList.length} UPC${upcList.length > 1 ? 's' : ''}`;
                    }
                    return response;
                };
            case ImportingDataTypes.partVisibility:
                return async (...args) => {
                    const request = args[0];
                    return await Api.setPartVisibilityByPartNumber({models: request?.models.filter(e => !!e)}, false);
                };
            case ImportingDataTypes.partPriceLevels:
                return (...args) => Api.setProductMultiplePriceLevelPrices(args[0], false);
            case ImportingDataTypes.partSerializability:
                return async (request) => {
                    const MAX_TOGGLE_ATTEMPTS = 2;
                    const normalizePartSerializabilityImportRequest = (request) => {
                        if (request == null) {
                            return null;
                        }
                        if (typeof request === 'number') {
                            return {partId: request, isSerialized: undefined};
                        }
                        const partId = request?.partId;
                        if (partId == null) {
                            return null;
                        }
                        return {
                            partId,
                            isSerialized: request?.isSerialized,
                        };
                    };
                    const normalizedRequest = normalizePartSerializabilityImportRequest(request);
                    if (!normalizedRequest?.partId) {
                        return {
                            resultFlag: false,
                            message: 'Missing part ID for serializability import.',
                        };
                    }

                    const {partId, isSerialized: targetIsSerialized} = normalizedRequest;
                    let response;

                    for (let attempt = 0; attempt < MAX_TOGGLE_ATTEMPTS; attempt++) {
                        response = await Api.toggleProductBeingSerialized(partId, false);
                        if (!response?.resultFlag) {
                            return response;
                        }

                        const resultingIsSerialized = getResultingIsSerializedFromToggle(response?.data);
                        if (
                            targetIsSerialized === undefined
                            || resultingIsSerialized === targetIsSerialized
                        ) {
                            return response;
                        }
                    }

                    return {
                        ...response,
                        resultFlag: false,
                        message: response?.message
                            || `Serializability could not be set to ${targetIsSerialized}.`,
                    };
                };
            case ImportingDataTypes.partDeliveryMode:
                return (...args) => Api.updatePartDeliveryMode(args[0]);
            case ImportingDataTypes.partOnlineOrderEligibility:
                return (...args) => Api.toggleProductEligibilityForOnlineOrdering(args[0]);
            case ImportingDataTypes.productVisibilityToOtherPartners:
                return async (request) => {
                    const response = await Api.setProductIsPublicForBuyers({
                        partId: request.partId,
                        isPublicForBuyers: request.isPublicForBuyers,
                    });
                    if (response?.resultFlag) {
                        void Api.markPartsForCache({
                            partIds: [request.partId],
                            sectionNames: [MarkProductForCacheSectionNames.shareStatus],
                        });
                    }
                    return response;
                };
            case ImportingDataTypes.customersTerritoriesAndAccountManagers:
                return async (...args) => {
                    const request = args[0];
                    return await Api.importUpdateCustomerOwner({dataList: request?.dataList.filter(e => !!e)}, false);
                };
            case ImportingDataTypes.warehouseAlertThresholds:
                return (...args) => Api.updatePartWarehouse(args[0], false);
            case ImportingDataTypes.productDimensions:
                return async (...args) => {
                    const request = args[0];
                    return await Api.setPartSizes({partId: request?.partId, sizes: request?.sizes.filter(e => !!e)}, false);
                };
            case ImportingDataTypes.productVendors:
                return async (...args) => {
                    const request = args[0];
                    if (request.id) {
                        return await Api.updateVendorPart(request, false);
                    } else {
                        return await Api.createVendorPart(request, false);
                    }
                };
            case ImportingDataTypes.priceMatrices:
                return (...args) => Api.createPriceMatrix(args[0], false);
            case ImportingDataTypes.externalCustomers:
                return async (...args) => {
                    const request = args[0];
                    if (!request.internalId) {
                        // we first have to fetch the Id from customerCode
                        const response = await Api.externalCustomerAutoFill({
                            keyword: request.customerCode,
                            pagination: {pageSize: 20, currentPage: 1},
                        }, false);
                        const matching = response.data.items?.filter(e => e.customerNo?.toLowerCase() === request.customerCode?.toLowerCase()) ?? [];
                        if (!response?.resultFlag || !matching?.length) {
                            return {
                                resultFlag: false,
                                message: "Product's internal ID could not be found based on the provided part number",
                            }
                        }
                        request.internalId = matching[0].internalId;
                    }
                    return await Api.importCustomerFromExternal(request, false);
                };
            case ImportingDataTypes.externalVendors:
                return async (...args) => {
                    const request = args[0];
                    return await Api.importVendorFromExternalService({
                        code: request.code,
                    }, false);
                };
            case ImportingDataTypes.productCategoryAssignment:
                return (...args) => Api.assignPartCategoryByTitle(args[0], false);
            case ImportingDataTypes.productsFromAnotherPartner:
                return async (request) => {
                    const copyRes = await Api.copyPartFromSellerPartner(
                        {
                            partId: request.partId,
                            partnerConnectionId: request.partnerConnectionId,
                            partNumber: request.partNumber,
                            keepUpdate: request.keepUpdate,
                        },
                        false,
                    );
                    if (!copyRes?.resultFlag) {
                        return copyRes;
                    }

                    const warehouseIds = request.warehouseIds ?? [];
                    const warehouseLabels = request.warehouseLabels ?? [];
                    if (!warehouseIds.length) {
                        return copyRes;
                    }

                    const localPartId = copyRes?.data?.partId;
                    if (localPartId == null || localPartId === '') {
                        return {
                            ...copyRes,
                            resultFlag: true,
                            message: t('warehouseImportMissingPartId'),
                        };
                    }

                    const warehouseErrors = [];
                    for (let i = 0; i < warehouseIds.length; i++) {
                        const warehouseId = warehouseIds[i];
                        const warehouseLabel = warehouseLabels[i] ?? String(warehouseId);
                        const whRes = await Api.createPartInWarehouse(
                            {partId: localPartId, warehouseId},
                            false,
                        );
                        if (whRes?.resultFlag) {
                            continue;
                        }
                        const errorCode = whRes?.errorCode ?? whRes?.code;
                        if (errorCode === ApiResponseCodes.spirePartAlreadyInWarehouse) {
                            continue;
                        }
                        warehouseErrors.push({
                            warehouse: warehouseLabel,
                            message: whRes?.message || Texts.invalidData,
                        });
                    }

                    if (!warehouseErrors.length) {
                        return {
                            ...copyRes,
                            resultFlag: true,
                            message: '',
                        };
                    }

                    const details = warehouseErrors
                        .map((entry, index) => t('warehouseImportErrorItem', {
                            index: index + 1,
                            warehouse: entry.warehouse,
                            message: entry.message,
                        }))
                        .join(' ');

                    return {
                        ...copyRes,
                        resultFlag: true,
                        message: t('warehouseImportPartialSuccess', {details}),
                    };
                };
            case ImportingDataTypes.removeProduct:
                return (...args) => Api.removeProduct(args[0], false);
            case ImportingDataTypes.productFlatShippingRates:
                return (...args) => Api.savePartRate(args[0], false);
            case ImportingDataTypes.productWarehouseAssignment:
                return async (request) =>
                    Api.createPartInWarehouse({
                        partId: request.partId,
                        warehouseId: request.warehouseId,
                    }, false);
            case ImportingDataTypes.setInventoryAvailability:
                return async (request) => Api.setInventoryAvailability({
                    whse: request?.whse,
                    items: (request?.items ?? []).map(({partNo, qty, averageCost, serialNumbers}) => ({
                        partNo,
                        qty,
                        averageCost,
                        serialNumbers,
                    })),
                }, false);
            case ImportingDataTypes.productFormAssignment:
                return async (request) => {
                    if (request?.operation === "update" && request?.partFormId != null) {
                        return Api.updatePartForm(
                            {id: request.partFormId, isRequired: !!request.isRequired},
                            false,
                        );
                    }
                    return Api.addPartForm(
                        {
                            partId: request.partId,
                            formId: request.formId,
                            isRequired: !!request.isRequired,
                        },
                        false,
                    );
                };
            case ImportingDataTypes.productRelationAssignment:
                return async (request) => {
                    const response = await Api.createOperatorPartRelation(
                        {
                            currentPartId: request.currentPartId,
                            relatedPartId: request.relatedPartId,
                            relationTypeId: request.relationTypeId,
                        },
                        false,
                    );
                    if (response?.resultFlag) {
                        void Api.markPartsForCache({
                            partIds: [request.currentPartId],
                            sectionNames: [MarkProductForCacheSectionNames.relations],
                        });
                    }
                    return response;
                };
            case ImportingDataTypes.productCodeAssignment:
                return async (request) => {
                    const assignments = request.codeAssignments ?? [];
                    const response = {
                        resultFlag: true,
                        message: '',
                    };
                    let addedCount = 0;
                    let skippedDuplicateCount = 0;
                    const failedMessages = [];

                    for (const assignment of assignments) {
                        for (const code of assignment.codes ?? []) {
                            const addResponse = await Api.createPartCodeValue({
                                code,
                                partId: request.partId,
                                partCodeCategoryId: assignment.partCodeCategoryId,
                            }, false);

                            if (addResponse?.resultFlag) {
                                addedCount++;
                            } else if (isDuplicatePartCodeApiResponse(addResponse)) {
                                skippedDuplicateCount++;
                            } else {
                                response.resultFlag = false;
                                failedMessages.push(`${code} (${addResponse?.message || 'Error'})`);
                            }
                        }
                    }

                    const messageParts = [];
                    if (addedCount) {
                        messageParts.push(`Successfully added ${addedCount} code${addedCount > 1 ? 's' : ''}`);
                    }
                    if (skippedDuplicateCount) {
                        messageParts.push(`Skipped ${skippedDuplicateCount} duplicate code${skippedDuplicateCount > 1 ? 's' : ''} already on part`);
                    }

                    if (response.resultFlag) {
                        response.message = messageParts.join('. ') || 'No codes to import';
                    } else {
                        response.message = `Failed: ${failedMessages.join(', ')}`;
                        if (skippedDuplicateCount) {
                            response.message += `. Skipped ${skippedDuplicateCount} duplicate code${skippedDuplicateCount > 1 ? 's' : ''} already on part`;
                        }
                    }

                    if (addedCount > 0) {
                        void Api.markPartsForCache({
                            partIds: [request.partId],
                            sectionNames: [MarkProductForCacheSectionNames.code],
                        });
                    }

                    return response;
                };
            default:
                if (state.selectedDataType.id === ImportingDataTypes.partTechnicalInformation) {
                    return (...args) => Api.setPartPropertyValues(args[0], false);
                }
                if (state.selectedDataType.id === ImportingDataTypes.productProperties) {
                    return (...args) => Api.createCustomIndustryProperty(args[0], false);
                }
                if (state.selectedDataType.id === ImportingDataTypes.productPropertyOptions) {
                    return async (...args) => {
                        const request = args[0];
                        let overallSuccess = true;
                        let errorMessages = [];
                        if (request.updateConfig) {
                            const configRes = await Api.setIndustryPropertyConfig({
                                id: request.propertyId,
                                hasPreDefinedValues: request.hasPreDefinedValues,
                                isMultiSelect: request.isMultiSelect,
                            }, false);
                            if (!configRes?.resultFlag) {
                                errorMessages.push(configRes?.message || 'Failed to update property configuration');
                                return {
                                    resultFlag: false,
                                    message: errorMessages.join(' | '),
                                    data: request,
                                }
                            }
                        }

                        if (request.newOptions?.length) {
                            for (const option of request.newOptions) {
                                const optRes = await Api.addIndustryPropertyPropertyValue({
                                    content: option,
                                    industryPropertyId: request.propertyId
                                }, false);
                                if (!optRes?.resultFlag) {
                                    overallSuccess = false;
                                    errorMessages.push(`Option "${option}" failed: ${optRes?.message || 'Error'}`);
                                }
                            }
                        }

                        return {
                            resultFlag: overallSuccess,
                            data: request,
                            message: errorMessages.join(' | ')
                        };
                    };
                }
                if (state.selectedDataType.id === ImportingDataTypes.productTagAssignment) {
                    return async (request) => {
                        const response = await Api.addTagToOperatorPart({
                            partId: request.partId,
                            tagId: request.tagId,
                        }, false);
                        if (response?.resultFlag) {
                            void Api.markPartsForCache({
                                partIds: [request.partId],
                                sectionNames: [MarkProductForCacheSectionNames.tag],
                            });
                        }
                        return response;
                    };
                }
                if (state.selectedDataType.id === ImportingDataTypes.externalProducts) {
                    return async (...args) => {
                        const request = args[0];
                        if (!request.internalId) {
                            // we first have to fetch the Id from partNo
                            const response = await Api.externalPartAutoFill({
                                keyword: request.partNumber,
                                pagination: {pageSize: 20, currentPage: 1},
                            }, undefined, false);
                            const matching = response.data.items?.filter(e => e.partNo?.toLowerCase() === request.partNumber?.toLowerCase()) ?? [];
                            if (!response?.resultFlag || !matching?.length) {
                                return {
                                    resultFlag: false,
                                    message: "Product's internal ID could not be found based on the provided part number",
                                }
                            }
                            request.internalId = matching[0].internalId;
                        }
                        let response = await Api.importPartFromExternal(request);
                        if (response?.errorCode === ApiResponseCodes.defaultCategoryRequiresForIndustry) {
                            // create a default category for the request.industryId
                            const categoryResponse = await Api.createPartCategory({
                                title: "Default",
                                industryId: request.industryId,
                            }, undefined, undefined, false)
                            if (categoryResponse?.resultFlag) {
                                response = await Api.importPartFromExternal(request);
                            }
                        }
                        return response;
                    }
                }
                return () => void 0;
        }
    }, [getBatchedApiRequestRecords, state.selectedDataType, t]);


    /**
     * Processes a single record import
     */
    const processRequest = useCallback(async ({selectedDataType, importedFile, apiRequest, importedFileIndex}) => {
        // Special handling for visibility imports
        // Note for batched data we do not have correction flow.
        if (isDataTypeApiCallsBatched(selectedDataType.id)) {

            if (apiRequest === null) {
                return;
            }

            let requestToSend = apiRequest;
            if (selectedDataType.id === ImportingDataTypes.setInventoryAvailability) {
                const kept = (apiRequest.rowIndices ?? []).map((rowIndex, itemIndex) => ({
                    rowIndex,
                    item: apiRequest.items?.[itemIndex],
                })).filter(({rowIndex, item}) =>
                    item != null && !userSkippedRowIndexSet.has(rowIndex),
                );
                if (!kept.length) {
                    return;
                }
                requestToSend = {
                    ...apiRequest,
                    items: kept.map((entry) => entry.item),
                    rowIndices: kept.map((entry) => entry.rowIndex),
                };
            }

            const response = await apiCallback(requestToSend);
            if (!isMounted()) return;

            const entities = [];
            const batchedRecords = getBatchedApiRequestRecords(selectedDataType, requestToSend);
            const batchRowIndices = requestToSend?.rowIndices;
            for (let i = 0; i < batchedRecords.length; i++) {
                const fileRowIndex = batchRowIndices?.[i] ?? (importedFileIndex + i);
                if (userSkippedRowIndexSet.has(fileRowIndex)) {
                    entities.push({
                        ...importedFile[fileRowIndex],
                        ...(buildSkippedFieldsColumnValue(fileRowIndex)
                            ? {'Skipped Fields': buildSkippedFieldsColumnValue(fileRowIndex)}
                            : {}),
                        id: createUUId(),
                        status: false,
                        errorMessage: IMPORT_SKIPPED_BY_USER_MESSAGE,
                        __raw__: {rowIndex: fileRowIndex},
                    });
                    continue;
                }
                if (batchedRecords[i] === null || batchedRecords[i] === undefined) {
                    const isObsolete = obsoleteRowIndexSet.has(fileRowIndex);
                    entities.push({
                        ...importedFile[fileRowIndex],
                        ...(buildSkippedFieldsColumnValue(fileRowIndex)
                            ? {'Skipped Fields': buildSkippedFieldsColumnValue(fileRowIndex)}
                            : {}),
                        id: createUUId(),
                        status: false,
                        errorMessage: isObsolete ? IMPORT_NO_IMPORTABLE_DATA_MESSAGE : 'Skipped: Invalid Data',
                        __raw__: {rowIndex: fileRowIndex},
                    });
                    continue;
                }
                const requestRecord = selectedDataType.id === ImportingDataTypes.setInventoryAvailability
                    ? {...batchedRecords[i], __batchItemIndex: i}
                    : batchedRecords[i];
                const entity = createResponseEntity({
                    importedFileEntry: importedFile[fileRowIndex],
                    response: response,
                    selectedDataType: selectedDataType,
                    requestRecord: requestRecord,
                    rowIndex: fileRowIndex,
                });
                const skippedFieldsValue = buildSkippedFieldsColumnValue(fileRowIndex);
                entities.push({
                    ...entity,
                    ...(skippedFieldsValue ? {'Skipped Fields': skippedFieldsValue} : {}),
                    __raw__: {
                        ...(entity.__raw__ ?? {}),
                        ...(skippedFieldsValue
                            ? {skippedFields: skippedFieldsValue.split('; ').filter(Boolean)}
                            : {}),
                    },
                });
            }
            setProcessedData(prevState => [...entities, ...prevState]);
        } else {

            const importedFileEntry = importedFile[importedFileIndex];

            if (userSkippedRowIndexSet.has(importedFileIndex)) {
                if (!isMounted()) return;
                recordSkippedByUser(importedFileEntry, importedFileIndex);
                return;
            }

            // If the transformer pre-nullified this row (due to preventative issues during final run),
            // record it as skipped in the processed data so it appears in the final report.
            if (apiRequest === null) {
                if (!isMounted()) return;
                const isObsolete = obsoleteRowIndexSet.has(importedFileIndex);
                recordSkippedRowOutcome(importedFileEntry, {
                    errorMessage: isObsolete ? IMPORT_NO_IMPORTABLE_DATA_MESSAGE : 'Skipped: Invalid Data',
                    skipReason: isObsolete ? 'obsolete' : 'invalid_data',
                    rowIndex: importedFileIndex,
                });
                return;
            }

            // Process valid data — first attempt
            let currentRequest = apiRequest;
            let response = await apiCallback(currentRequest);
            if (!isMounted()) return;

            // ── Interactive correction check ───────────────────────────────────
            // If the response failed and interactive correction is enabled, give the user the chance to fix the record and retry. A while loop is used to allow fixing sequential/different errors
            // on the same record.
            let maxInteractiveRetries = 5;
            while (!response?.resultFlag && getInteractiveCorrectionEnabled() && maxInteractiveRetries > 0) {
                const correctionPromise = requestCorrectionIfNeeded({
                    apiRequest: currentRequest,
                    response,
                });

                if (!correctionPromise) break; // Error not covered by interactive configs, exit loop

                // Pause the loop while awaiting user input
                isPausedRef.current = true;
                setProcessStats(ImportingProcessStatus.paused);

                const decision = await correctionPromise;  // blocks until user acts

                // Resume
                isPausedRef.current = false;
                setProcessStats(ImportingProcessStatus.loading);

                if (decision?.action === 'retry') {
                    // Re-run the API call with the user-corrected record
                    currentRequest = decision.updatedRecord;
                    response = await apiCallback(currentRequest);
                    if (!isMounted()) return;
                    maxInteractiveRetries--;
                } else {
                    // decision.action === 'skip' - breaks out of the loop and falls through to error recording
                    break;
                }
            }
            // ─────────────────────────────────────────────────────────────────

            // Record result
            const rawEntity = createResponseEntity({
                importedFileEntry: importedFileEntry,
                response: response,
                selectedDataType: selectedDataType,
                requestRecord: currentRequest,
                rowIndex: importedFileIndex,
            });

            // Compare the pristine initial state with the currentRequest to detect interactive corrections. We use `interactiveSections` (not columnMapping) because the fields therein have `field.key` =
            // the actual API request property name, and `field.label` = the display label that matches the column titles in the data grid.
            const pristineRequest = originalApiRequests[importedFileIndex];
            const corrections = {}; // keyed by display label (e.g. "Root Category")
            const changedHeaders = {}; // exported as "Changed Root Category" → new value

            if (pristineRequest && interactiveSections?.length) {
                for (const section of interactiveSections) {
                    for (const field of section.fields) {
                        const apiKey = field.key; // e.g. "parentCategoryTitle"
                        const displayLabel = field.label; // e.g. "Root Category"
                        if (
                            apiKey &&
                            displayLabel &&
                            currentRequest[apiKey] !== pristineRequest[apiKey]
                        ) {
                            corrections[apiKey] = {
                                old: pristineRequest[apiKey] ?? '',
                                new: currentRequest[apiKey] ?? '',
                            };
                            changedHeaders[`Changed ${displayLabel}`] = currentRequest[apiKey] ?? '';
                        }
                    }
                }
            }

            const skippedFieldsValue = buildSkippedFieldsColumnValue(importedFileIndex);

            const finalEntity = {
                ...rawEntity,
                ...changedHeaders, // appended as new CSV columns; data grid filter strips them from cells
                ...(skippedFieldsValue ? {'Skipped Fields': skippedFieldsValue} : {}),
            };
            if (finalEntity.__raw__) {
                finalEntity.__raw__.corrections = corrections;
                if (skippedFieldsValue) {
                    finalEntity.__raw__.skippedFields = skippedFieldsValue.split('; ').filter(Boolean);
                }
            }

            setProcessedData(prevState => [finalEntity, ...prevState]);
        }
    }, [isDataTypeApiCallsBatched, apiCallback, isMounted, getBatchedApiRequestRecords, userSkippedRowIndexSet, obsoleteRowIndexSet, createResponseEntity, IMPORT_SKIPPED_BY_USER_MESSAGE, IMPORT_NO_IMPORTABLE_DATA_MESSAGE, getInteractiveCorrectionEnabled, originalApiRequests, interactiveSections, recordSkippedByUser, recordSkippedRowOutcome, buildSkippedFieldsColumnValue, requestCorrectionIfNeeded, setProcessStats]);

    /**
     * Main import process handler
     */
    const importData = useCallback(async () => {
        const now = DateUtils.serverNow().valueOf();
        const requestCount = apiRequestsRef.current?.length ?? 0;
        const executionOrder =
            executionOrderRef.current ??
            Array.from({length: requestCount}, (_, index) => index);

        for (let step = currentIndexRef.current; step < executionOrder.length; step++) {
            const importedFileIndex = executionOrder[step];
            if (!isMounted())
                return;
            if (isPausedRef.current) break;

            await Promise.all([
                processRequest({
                    selectedDataType: state.selectedDataType,
                    importedFile: importedFileRef.current,
                    apiRequest: apiRequestsRef.current[importedFileIndex],
                    importedFileIndex,
                }),
                Utils.wait(100)
            ]);
            currentIndexRef.current = step + 1;
        }
        if (!isMounted()) return;
        if (isPausedRef.current) {
            timeSpentInMs.current += (DateUtils.serverNow().valueOf() - now);
            return;
        }
        setProcessStats(ImportingProcessStatus.finished);
        timeSpentInMs.current += (DateUtils.serverNow().valueOf() - now);
    }, [isMounted, setProcessStats, state.selectedDataType, processRequest]);

    /**
     * Pauses the importing process.
     * @type {(function(): void)|*}
     */
    const pauseImport = useCallback(() => {
        isPausedRef.current = true;
        setProcessStats(ImportingProcessStatus.paused);
    }, [setProcessStats]);

    /**
     * Resumes the importing process.
     * @type {(function(): void)|*}
     */
    const resumeImport = useCallback(() => {
        isPausedRef.current = false;
        setProcessStats(ImportingProcessStatus.loading);
        importData().then();
    }, [setProcessStats, importData]);

    /**
     * Initiates import process on component mount
     */
    useEffect(() => {
        if (currentIndexRef.current === 0 && processStatus !== ImportingProcessStatus.paused && processStatus !== ImportingProcessStatus.finished) {
            setProcessStats(ImportingProcessStatus.loading);
            importData().then();
        }
    }, [importData, setProcessStats, processStatus]);

    /**
     * Calculates import progress percentage
     */
    const progressPercentage = useMemo(() => {
        return Math.min(Math.round(paginationInfo.length / Math.max(importProgressTotal, 1) * 100), 100);
    }, [paginationInfo.length, importProgressTotal]);

    /**
     * Formats time duration into readable string
     */
    const getTimeSpentInReadableFormat = useCallback((timeInMs) => {
        let timeInSeconds = timeInMs / 1000;
        const hours = Math.floor(timeInSeconds / 3600);
        timeInSeconds = timeInSeconds % 3600;
        const minutes = Math.floor(timeInSeconds / 60);
        timeInSeconds = timeInSeconds % 60;
        const seconds = Math.floor(timeInSeconds);
        timeInSeconds = timeInSeconds % 1;
        const milliseconds = Math.floor(timeInSeconds * 1000);
        return `${hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''} ` : ''}${minutes > 0 ? `${minutes} minute${minutes > 1 ? 's' : ''} ` : ''}${seconds > 0 ? `${seconds} second${seconds > 1 ? 's' : ''} ` : ''} ${milliseconds > 0 ? `${milliseconds} millisecond${seconds > 1 ? 's' : ''} ` : ''}`;
    }, []);

    /**
     * Handles report export functionality
     */
    const exportReport = useCallback(async (type) => {
        setExporting(true);
        const data = [...processedData];
        data.reverse();
        let entries = data.map((entry) => ({
            ...Object.fromEntries(
                Object.entries(entry)
                    .filter(([key]) => !['id', 'status', 'errorMessage', '__raw__'].includes(key))
                    .map(([key, value]) => {
                        const rowIndex = entry.__raw__?.rowIndex;
                        if (rowIndex != null && isImportedFieldSkipped(rowIndex, key)) {
                            return [key, t('fieldSkippedCell')];
                        }
                        return [key, value === 'Skipped' ? '' : value];
                    }),
            ),
            Status: entry.status ? 'Passed' : 'Failed',
            'Error Message': entry.errorMessage,
        }));

        let title;
        switch (type) {
            case "all":
                title = `Import ${state.selectedDataType.title} All Records Report`;
                break;
            case "failed":
                entries = entries.filter(e => e.Status === "Failed");
                title = `Import ${state.selectedDataType.title} Failed Records Report`;
                break;
            case "passed":
                entries = entries.filter(e => e.Status === "Passed");
                title = `Import ${state.selectedDataType.title} Passed Records Report`;
                break;
            case "skipped": {
                const processedSkipped = processedData
                    .filter((entry) =>
                        !entry.status &&
                        (
                            entry.errorMessage === IMPORT_SKIPPED_BY_USER_MESSAGE ||
                            entry.errorMessage === IMPORT_NO_IMPORTABLE_DATA_MESSAGE ||
                            entry.errorMessage === 'Skipped: Invalid Data' ||
                            !!entry['Skipped Fields']
                        ),
                    )
                    .map((entry) => ({
                        ...Object.fromEntries(
                            Object.entries(entry)
                                .filter(([key]) => !['id', 'status', 'errorMessage', '__raw__'].includes(key))
                                .map(([key, value]) => [key, value === 'Skipped' ? '' : value]),
                        ),
                        Status: 'Failed',
                        'Error Message': entry.errorMessage,
                    }));

                const buildMappedRowFingerprint = (entry) => mappedFields
                    .map((field) => entry[field.name]?.toString()?.trim()?.toLowerCase() ?? '')
                    .join('|');

                const processedKeys = new Set(
                    processedSkipped.map((entry) => buildMappedRowFingerprint(entry)),
                );

                const unprocessedSkipped = (state.importedFile ?? [])
                    .filter((importedFileEntry) => {
                        const key = buildMappedRowFingerprint(importedFileEntry);
                        return !processedKeys.has(key) &&
                            !processedData.some((processedEntry) =>
                                mappedFields.every((field) =>
                                    (processedEntry[field.name]?.toString()?.trim()?.toLowerCase() ?? '') ===
                                    (importedFileEntry[field.name]?.toString()?.trim()?.toLowerCase() ?? ''),
                                ),
                            );
                    })
                    .map((entry) => ({
                        ...entry,
                        Status: 'Failed',
                        'Error Message': 'Not processed',
                    }));

                entries = [...processedSkipped, ...unprocessedSkipped];
                title = `Import ${state.selectedDataType.title} Skipped Records Report`;
                break;
            }
            case "conflicting-product-vendor-records": {
                entries = processedData
                    .filter(e => e.__raw__?.response?.errorCode === ApiResponseCodes.vendorPartConflictingRecord)
                    .map(e => e.__raw__)
                    .map(({response, request}) => ({
                        "Part Number": request.partNumber,
                        "Vendor Code": request.vendorCode,
                        "Vendor Part Number": request.vendorPartNumber,
                        "Vendor Price": request.vendorPrice,
                        "Start Date": request.startDateTime ? DateUtils.serverToLocal(request.startDateTime).format(DateUtils.withoutOffsetFormat) : '',
                        "End Date": request.endDateTime ? DateUtils.serverToLocal(request.endDateTime).format(DateUtils.withoutOffsetFormat) : '',
                        "Connection/Conflict ID": response?.data?.id ?? '',
                        "Conflicted With": [
                            `Vendor part Number: ${response?.data?.vendorPartNumber ?? ''}`,
                            `Vendor Price: ${response?.data?.vendorPrice ?? ''}`,
                            `Start Date: ${response?.data?.startDateTime ? DateUtils.serverToLocal(response.data.startDateTime).format(DateUtils.withoutOffsetFormat) : ''}`,
                            `End Date: ${response?.data?.endDateTime ? DateUtils.serverToLocal(response.data.endDateTime).format(DateUtils.withoutOffsetFormat) : ''}`

                        ].join(' | ')
                    }))
                title = `Import ${state.selectedDataType.title} Conflicting Product/Vendor Records Report`;
                break;
            }
            case "set-availability-report": {
                entries = processedData
                    .filter((entry) => entry.status)
                    .map((entry) => {
                        const responseItem = entry.__raw__?.responseItem ?? {};
                        const imported = entry.__raw__?.importedFileEntry ?? entry;
                        return {
                            'Warehouse Code': imported['Warehouse Code'] ?? '',
                            'Part No': imported['Part No'] ?? '',
                            'Quantity': imported['Quantity'] ?? '',
                            'Avg Cost': imported['Avg Cost'] ?? '',
                            'Serial Numbers': imported['Serial Numbers'] ?? '',
                            'From Qty': responseItem.fromQty ?? entry['From Qty'] ?? '',
                            'To Qty': responseItem.toQty ?? entry['To Qty'] ?? '',
                            'Delta': responseItem.delta ?? entry['Delta'] ?? '',
                            'Serials Added': joinDelimitedList(responseItem.serialsAdded, serialImportSplitter) || entry['Serials Added'] || '',
                            'Serials Removed': joinDelimitedList(responseItem.serialsRemoved, serialImportSplitter) || entry['Serials Removed'] || '',
                        };
                    });
                title = t('setAvailabilityReportTitle', {datasetTitle: state.selectedDataType.title});
                break;
            }
            case "associated-adjustments-report": {
                const seenAdjustmentKeys = new Set();
                entries = [];
                for (const entry of processedData.filter((row) => row.status)) {
                    const responseData = entry.__raw__?.response?.data;
                    const adjustments = responseData?.adjustments ?? [];
                    if (!adjustments.length) {
                        continue;
                    }
                    const warehouseCode =
                        responseData?.whse
                        ?? entry.__raw__?.importedFileEntry?.['Warehouse Code']
                        ?? '';
                    const partNumbers = (responseData?.items ?? [])
                        .map((item) => item?.partNo)
                        .filter(Boolean)
                        .join(serialImportSplitter || ', ');
                    for (const adjustment of adjustments) {
                        const key = `${warehouseCode}|${adjustment?.adjustmentId ?? ''}|${adjustment?.adjustmentNo ?? ''}`;
                        if (seenAdjustmentKeys.has(key)) {
                            continue;
                        }
                        seenAdjustmentKeys.add(key);
                        entries.push({
                            'Warehouse Code': warehouseCode,
                            'Adjustment Id': adjustment?.adjustmentId ?? '',
                            'Adjustment No': adjustment?.adjustmentNo ?? '',
                            'Posted': responseData?.posted === true
                                ? 'Yes'
                                : responseData?.posted === false
                                    ? 'No'
                                    : '',
                            'Part Numbers': partNumbers,
                        });
                    }
                }
                title = t('associatedAdjustmentsReportTitle', {datasetTitle: state.selectedDataType.title});
                break;
            }
            default:
                title = `Import ${state.selectedDataType.title} Records Report`;
                break;
        }
        if (!entries.length) {
            ToastService.warning({message: "No records found for exporting!"});
            setExporting(false);
            return;
        }
        exportCSVFile(entries, title);
        setExporting(false);
    }, [processedData, state.selectedDataType.title, state.importedFile, mappedFields, IMPORT_SKIPPED_BY_USER_MESSAGE, IMPORT_NO_IMPORTABLE_DATA_MESSAGE, isImportedFieldSkipped, serialImportSplitter, t]);

    /**
     * Handles search keyword updates
     */
    const handleSearch = useCallback((val) => {
        setKeyword(val);
    }, []);

    /**
     * Handles pagination updates
     */
    const handleCurrentPageChanged = useCallback((_, current) => {
        _setPaginationInfo({...current, sizes: initialPaginationInfo.sizes});
    }, [initialPaginationInfo.sizes]);

    const handlePageSizeChanged = useCallback((_, current) => {
        _setPaginationInfo({...current, sizes: initialPaginationInfo.sizes});
    }, [initialPaginationInfo.sizes]);

    /**
     * Renders export buttons block
     */
    const renderExportButtons = useCallback((isPaused) => {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: isPaused ? 'row' : 'column',
                flexWrap: isPaused ? 'wrap' : 'nowrap',
                justifyContent: isPaused ? 'center' : 'flex-start',
                alignItems: isPaused ? 'center' : 'flex-start',
                mt: isPaused ? 3 : 2,
                gap: isPaused ? 2 : 0
            }}>
                <Button
                    className={`summary-action ${!isPaused ? 'text-align-left' : ''}`}
                    onClick={() => exportReport('all')}
                    disabled={exporting}
                    variant={isPaused ? 'outlined' : 'text'}
                    color={'primary'}
                >
                    {exporting ? <CircularProgress size={15} color={'primary'} className={'mr-2'}/> : <SvgIcon className='icon normal mr-2' component={Excel}/>}
                    {exporting ? 'Downloading...' : 'Download All Processed Records Report'}
                </Button>
                {
                    !!processedData.filter(e => e.status).length &&
                    <Button
                        className={`summary-action ${!isPaused ? 'text-align-left' : ''}`}
                        onClick={() => exportReport('passed')}
                        disabled={exporting}
                        variant={isPaused ? 'outlined' : 'text'}
                        color={'success'}
                    >
                        {exporting ? <CircularProgress size={15} color={'primary'} className={'mr-2'}/> : <SvgIcon className='icon normal mr-2' component={Excel}/>}
                        {exporting ? 'Downloading...' : 'Download Successful Records Report'}
                    </Button>
                }
                {
                    !!processedData.filter(e => !e.status).length &&
                    <Button
                        className={`summary-action ${!isPaused ? 'text-align-left' : ''}`}
                        onClick={() => exportReport('failed')}
                        disabled={exporting}
                        variant={isPaused ? 'outlined' : 'text'}
                        color={'error'}
                    >
                        {exporting ? <CircularProgress size={15} color={'primary'} className={'mr-2'}/> : <SvgIcon className='icon normal mr-2' component={Excel}/>}
                        {exporting ? 'Downloading...' : 'Download Failed Records Report'}
                    </Button>
                }
                {
                    !!state.importedFile.length && state.importedFile.length > usableRecords &&
                    <Button
                        className={`summary-action ${!isPaused ? 'text-align-left' : ''}`}
                        onClick={() => exportReport('skipped')}
                        disabled={exporting}
                        variant={isPaused ? 'outlined' : 'text'}
                        color={'warning'}
                    >
                        {exporting ? <CircularProgress size={15} color={'primary'} className={'mr-2'}/> : <SvgIcon className='icon normal mr-2' component={Excel}/>}
                        {exporting ? 'Downloading...' : 'Download Skipped (Invalid) Records Report'}
                    </Button>
                }
                {
                    state.selectedDataType?.id === ImportingDataTypes.setInventoryAvailability &&
                    !!processedData.filter(e => e.status).length &&
                    <Button
                        className={`summary-action text-align-left`}
                        onClick={() => exportReport('set-availability-report')}
                        disabled={exporting}
                        variant={isPaused ? 'outlined' : 'text'}
                        color={'primary'}
                    >
                        {exporting ? <CircularProgress size={15} color={'primary'} className={'mr-2'}/> : <SvgIcon className='icon normal mr-2' component={Excel}/>}
                        {exporting ? t('exportDownloading') : t('exportSetAvailabilityReport')}
                    </Button>
                }
                {
                    state.selectedDataType?.id === ImportingDataTypes.setInventoryAvailability &&
                    !!processedData.some((entry) =>
                        entry.status && (entry.__raw__?.response?.data?.adjustments?.length ?? 0) > 0,
                    ) &&
                    <Button
                        className={`summary-action text-align-left`}
                        onClick={() => exportReport('associated-adjustments-report')}
                        disabled={exporting}
                        variant={isPaused ? 'outlined' : 'text'}
                        color={'primary'}
                    >
                        {exporting ? <CircularProgress size={15} color={'primary'} className={'mr-2'}/> : <SvgIcon className='icon normal mr-2' component={Excel}/>}
                        {exporting ? t('exportDownloading') : t('exportAssociatedAdjustmentsReport')}
                    </Button>
                }
                {
                    state.selectedDataType?.id === ImportingDataTypes.productVendors &&
                    !!state.importedFile.length &&
                    <Button
                        className={`summary-action text-align-left`}
                        onClick={() => exportReport('conflicting-product-vendor-records')}
                        disabled={exporting}
                        variant={isPaused ? 'outlined' : 'text'}
                        color={'warning'}
                    >
                        {exporting ? <CircularProgress size={15} color={'primary'} className={'mr-2'}/> : <SvgIcon className='icon normal mr-2' component={Excel}/>}
                        {exporting ? 'Downloading...' : 'Download Conflicting Records'}
                    </Button>
                }
            </Box>
        );
    }, [processedData, exporting, exportReport, state.importedFile.length, usableRecords, state.selectedDataType?.id, t]);

    const importedFileRef = useRef(state.importedFile);
    const apiRequestsRef = useRef(state.apiRequests);

    const executionOrderRef = useRef(null);

    /**
     * Track current state properties in a ref so the async loop can access fresh data when `state.apiRequests` updates during mid-loop
     */
    useEffect(() => {
        importedFileRef.current = state.importedFile;
        apiRequestsRef.current = state.apiRequests;
        executionOrderRef.current =
            state.selectedDataType?.id === ImportingDataTypes.productWarehouseAssignment
                ? getProductWarehouseAssignmentExecutionOrder(state.apiRequests)
                : null;
    }, [state.importedFile, state.apiRequests, state.selectedDataType?.id]);


    /**
     * Prepares data grid rows with formatted cells
     */
    const dataGridRows = useMemo(() => {
        const renderMappedValueCell = (data, key, value) => {
            const rowIndex = data.__raw__?.rowIndex;
            if (isImportedFieldSkipped(rowIndex, key)) {
                return (
                    <Tooltip placement={'top-start'} title={t('fieldSkippedCell')}>
                        <div className={'d-flex flex-column justify-content-center'}>
                            <p
                                {...dataGridCellTextProps}
                                className={'data-grid-cell-text-ellipsis mb-0'}
                                style={{fontStyle: 'italic', opacity: 0.65}}
                            >
                                {t('fieldSkippedCell')}
                            </p>
                        </div>
                    </Tooltip>
                );
            }

            if (data.__raw__?.corrections?.[key]) {
                return (
                    <Tooltip placement={'top-start'} title={data[key]?.toString()}>
                        <div className={'d-flex flex-column justify-content-center'}>
                            <span style={{textDecoration: 'line-through', opacity: 0.6, fontSize: '0.85em', lineHeight: '1.2'}}>
                                {data.__raw__.corrections[key].old || '-'}
                            </span>
                            <span style={{color: 'var(--success-main)', fontWeight: 600, lineHeight: '1.2'}}>
                                {data.__raw__.corrections[key].new || '-'}
                            </span>
                        </div>
                    </Tooltip>
                );
            }

            return (
                <Tooltip placement={'top-start'} title={data[key]?.toString()}>
                    <div className={'d-flex flex-column justify-content-center'}>
                        <p {...dataGridCellTextProps} className={'data-grid-cell-text-ellipsis mb-0'}>
                            {value?.toString()}
                        </p>
                    </div>
                </Tooltip>
            );
        };

        return visibleData?.map(data => ({
            key: data.id,
            data: data,
            cells: {
                ...Object.fromEntries(
                    Object.entries(data)
                        .filter(([key]) => !['id', 'status', 'errorMessage', '__raw__'].includes(key) && !key.startsWith('Changed '))
                        .map(([key, value]) => [
                            key,
                            productPopoverFieldNames.has(key)
                                ? <ProductInformationPopover partNo={data.__raw__?.corrections?.[key] ? data.__raw__.corrections[key].new?.toString() : data[key]?.toString()}>
                                    <div className={'d-flex flex-column justify-content-center'}>
                                        {isImportedFieldSkipped(data.__raw__?.rowIndex, key) ? (
                                            <p
                                                {...dataGridCellTextProps}
                                                className={'data-grid-cell-text-ellipsis mb-0'}
                                                style={{fontStyle: 'italic', opacity: 0.65}}
                                            >
                                                {t('fieldSkippedCell')}
                                            </p>
                                        ) : data.__raw__?.corrections?.[key] ? (
                                            <>
                                                <span style={{textDecoration: 'line-through', opacity: 0.6, fontSize: '0.85em', lineHeight: '1.2'}}>
                                                    {data.__raw__.corrections[key].old || '-'}
                                                </span>
                                                <span style={{color: 'var(--success-main)', fontWeight: 600, lineHeight: '1.2'}}>
                                                    {data.__raw__.corrections[key].new || '-'}
                                                </span>
                                            </>
                                        ) : (
                                            <p {...dataGridCellTextProps} className={'data-grid-cell-text-ellipsis mb-0'}>
                                                {value?.toString()}
                                            </p>
                                        )}
                                    </div>
                                </ProductInformationPopover>
                                : renderMappedValueCell(data, key, value),
                        ])),
                "status": data.status ? <Passed/> : <Failed/>,
                "errorMessage": data.errorMessage
            }
        })) ?? [];
    }, [visibleData, isImportedFieldSkipped, productPopoverFieldNames, t]);

    return (
        <>
            <div className={'importing-process-section'}>
                {processStatus === ImportingProcessStatus.loading || processStatus === ImportingProcessStatus.paused ? (
                    // Loading / Paused State UI
                    <div className={'importing-in-process'}>
                        <ImportingLottieAnimation/>
                        <Tooltip placement={'top'} arrow title={`${processedData.length ?? 0} / ${importProgressTotal ?? 1}`}>
                            <div className={'d-flex flex-column w-100 py-3 align-items-center'}>
                                <LinearProgress
                                    className={'w-80'}
                                    variant={'buffer'}
                                    color={'secondary'}
                                    value={progressPercentage}
                                    valueBuffer={progressPercentage}
                                />
                                <p className={'identifier'}>{`${progressPercentage}%`}</p>
                            </div>
                        </Tooltip>
                        <p className={'warning-note'}>
                            {
                                processStatus === ImportingProcessStatus.paused
                                    ? "Importing process has been paused"
                                    : "We are importing your requested data, please be patient and do not close this page"
                            }
                        </p>
                        <div className={'d-flex mt-1 px-4'}>
                            <Warning/>
                            <p className={'leaving-warning-note'}>
                                By leaving this page, the import process will be stopped and you will not have
                                access to the report of the importing process later.
                            </p>
                        </div>
                        <div className={'d-flex mt-4 justify-content-center w-100'}>
                            {processStatus === ImportingProcessStatus.loading && (
                                <Button
                                    className={'mx-2'}
                                    variant={'contained'}
                                    color={'warning'}
                                    onClick={pauseImport}
                                >
                                    Pause Import
                                </Button>
                            )}
                            {processStatus === ImportingProcessStatus.paused && (
                                <Button
                                    className={'mx-2'}
                                    variant={'contained'}
                                    color={'primary'}
                                    onClick={resumeImport}
                                >
                                    Resume Import
                                </Button>
                            )}
                        </div>
                        {processStatus === ImportingProcessStatus.paused && renderExportButtons(true)}
                    </div>
                ) : (
                    // Results UI
                    <Row className={'importing-report-section'}>
                        {/* Status Summary */}
                        <Col xs={12} md={6} className={'d-flex flex-column align-items-center justify-content-center mb-5'}>
                            <div className={'mb-2'}>
                                {(processedData.filter(e => e.status)?.length ?? 0) === state.importedFile.length ? (
                                    <SuccessSvg/>
                                ) : (processedData.filter(e => !e.status)?.length ?? 0) === state.importedFile.length ? (
                                    <FailedSvg/>
                                ) : (
                                    <ProblemSvg/>
                                )}
                            </div>
                            <div className={'image-caption'}>
                                {(processedData.filter(e => e.status)?.length ?? 0) === state.importedFile.length ? (
                                    <p>All data have been imported successfully</p>
                                ) : (processedData.filter(e => !e.status)?.length ?? 0) === state.importedFile.length ? (
                                    <p>No data was imported successfully<br/>Please try again later</p>
                                ) : (
                                    <p>The request was successful<br/>However, some of the records have not been imported</p>
                                )}
                                <p className={'mt-2'}>Time spent: <span className={'time-spent'}>
                                    {getTimeSpentInReadableFormat(timeSpentInMs.current)}
                                </span></p>
                            </div>
                        </Col>

                        {/* Statistics and Export Options */}
                        <Col xs={12} md={6} className={'d-flex flex-column align-items-center justify-content-center'}>
                            <div className={'report-table'}>
                                <div className={'report-table-requested-part'}>
                                    <p>Imported File Records</p>
                                    <p>{state.importedFile.length}</p>
                                </div>
                                <div className={'report-table-requested-part'}>
                                    <p>Usable (Valid) Records</p>
                                    <p>{usableRecords}</p>
                                </div>
                                {(processedData.filter(e => e.status)?.length ?? 0) > 0 && (
                                    <div className={'report-table-success'}>
                                        <p>Successful Records</p>
                                        <p>{processedData.filter(e => e.status)?.length ?? 0}</p>
                                    </div>
                                )}
                                {(processedData.filter(e => !e.status)?.length ?? 0) > 0 && (
                                    <div className={'report-table-failed'}>
                                        <p>Failed Records</p>
                                        <p>{processedData.filter(e => !e.status)?.length ?? 0}</p>
                                    </div>
                                )}
                            </div>

                            {renderExportButtons(false)}
                        </Col>
                    </Row>
                )}

                {/* Search and Data Grid */}
                <div className={'separator'}>
                    <div/>
                    <div/>
                </div>
                <div className={'search-section'}>
                    <SearchInput
                        loading={false}
                        autoSearch={true}
                        search={handleSearch}
                        ariaLabel={'operator search'}
                        label={'Search'}
                    />
                    <div className={'separator'}/>
                </div>

                {/* Results Data Grid */}
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{columns: dataGridColumns}}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    hideFooterActions
                    hideExporting
                    minRowHeight={41}
                    state={{
                        pagination: paginationInfo,
                        density: dataGridDensity,
                    }}
                    onCurrentPageChanged={handleCurrentPageChanged}
                    onPageSizeChanged={handlePageSizeChanged}
                />
            </div>
            <InteractiveCorrectionDialog
                open={!!dialogState}
                dialogState={dialogState}
                onApplyAndRetry={applyAndRetry}
                onSkip={skip}
                interactiveCorrectionEnabled={interactiveCorrectionEnabled}
                setInteractiveCorrectionEnabled={setInteractiveCorrectionEnabled}
            />
        </>
    );
};

export default ImportingSection;
